import React, { lazy, Suspense } from 'react';

function formatDateRangeForDashboard(dateRange) {
    if (!dateRange) return '';
    if (typeof dateRange === 'string') return dateRange;
    if (typeof dateRange === 'object') {
        const start = dateRange.start_date || dateRange.startDate || dateRange.start || '';
        const end = dateRange.end_date || dateRange.endDate || dateRange.end || '';
        if (start && end) return `${start} to ${end}`;
        if (start) return String(start);
        if (end) return String(end);
    }
    try {
        return String(dateRange);
    } catch {
        return '';
    }
}

function resolveManifest(manifest) {
    if (manifest && typeof manifest === 'object' && (manifest.topics != null || manifest.overall != null)) {
        return manifest;
    }
    return { topics: {}, overall: {} };
}

const SAFE_FILE_BASE = /^[A-Za-z0-9_-]+$/;

const lazyByFileBase = new Map();

function getLazyForFileBase(fb) {
    if (!lazyByFileBase.has(fb)) {
        lazyByFileBase.set(
            fb,
            lazy(() =>
                import(
                    /* webpackChunkName: "report-generated-[request]" */
                    `../components/reports/generated/${fb}.tsx`
                ).catch(() =>
                    import(
                        /* webpackChunkName: "report-generated-[request]" */
                        `../components/reports/generated/${fb}.jsx`
                    ).catch(() => ({ default: () => null }))
                )
            )
        );
    }
    return lazyByFileBase.get(fb);
}

export function topicHasGeneratedDashboard(topicKey, manifest) {
    const m = resolveManifest(manifest);
    const fb = m?.topics?.[topicKey]?.fileBase;
    return !!(fb && SAFE_FILE_BASE.test(fb));
}

export function hasGeneratedOverallDashboard(databaseId, manifest) {
    const m = resolveManifest(manifest);
    const key = String(databaseId);
    const entry = m?.overall?.[key];
    if (!entry || typeof entry !== 'object') return false;
    const status = entry.status;
    if (status === 'in_progress' || status === 'failed') return false;
    const fb = entry.fileBase;
    return !!(fb && SAFE_FILE_BASE.test(fb));
}

/**
 * Returns the overall dashboard status entry for ``databaseId``.
 *
 * @returns {{ status?: 'in_progress'|'completed'|'failed', error?: string|null, fileBase?: string }}
 */
export function getOverallDashboardStatus(databaseId, manifest) {
    const m = resolveManifest(manifest);
    const key = String(databaseId);
    return m?.overall?.[key] ?? {};
}

export function GeneratedReportDashboard({
    topicKey,
    manifest,
    executedQueries,
    timeFrameLabel,
    dateRange,
}) {
    const m = resolveManifest(manifest);
    const fb = m?.topics?.[topicKey]?.fileBase;
    if (!fb || !SAFE_FILE_BASE.test(fb)) return null;
    const Comp = getLazyForFileBase(fb);
    const dateRangeLabel = formatDateRangeForDashboard(dateRange);
    return (
        <Suspense
            fallback={
                <div className="mb-6 rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-600">
                    Loading report visualization…
                </div>
            }
        >
            <Comp
                executedQueries={executedQueries}
                timeFrameLabel={timeFrameLabel}
                dateRange={dateRangeLabel}
            />
        </Suspense>
    );
}

export function GeneratedOverallDashboard({
    databaseId,
    manifest,
    reports,
    databaseName,
    onOpenPriorities,
}) {
    const m = resolveManifest(manifest);
    const key = String(databaseId ?? '');
    const fb = m?.overall?.[key]?.fileBase;
    if (!fb || !SAFE_FILE_BASE.test(fb)) return null;
    const Comp = getLazyForFileBase(fb);
    return (
        <Suspense
            fallback={
                <div className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-600">
                    Loading overall dashboard…
                </div>
            }
        >
            <Comp
                reports={reports}
                databaseName={databaseName}
                onOpenPriorities={onOpenPriorities}
            />
        </Suspense>
    );
}
